<template>
	<div v-if="educations" class="card-page education-wrapper">
		<ja-header
			:title="$t('gamePlayMetaTitles.education')"
			subtitle="JA Finance Park"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/education"></svgicon>
			</template>
		</ja-header>
		<content>
			<div class="educations-cnt">
				<h1 class="heading">{{ $t('educationTitle') }}</h1>
				<h2 class="sub-heading body-text-1">{{ $t('educationMsg') }}</h2>
				<div class="education-grid">
					<div
						v-for="education in educations"
						:key="education.education_ID"
						class="education-card"
					>
						<div class="heading-4">
							{{ education.selectionDisplayName }}
						</div>
						<div class="button-wrapper">
							<ja-button
								:name="education.selectionDisplayName"
								class="small outlined"
								:success="selectedEducation == education.education_ID"
								:aria-label="
									$t('selectEducation', {
										education: education.selectionDisplayName
									})
								"
								role="switch"
								:aria-checked="
									(selectedEducation == education.education_ID).toString()
								"
								@click.native="selectEducation(education)"
								>{{ $t('select') }}</ja-button
							>
						</div>
					</div>
				</div>
			</div>
		</content>
		<ja-alert-bar :alert-condition="isMilitarySelected">
			{{ $t('militaryMsg') }}
		</ja-alert-bar>
		<ja-alert-bar
			:alert-condition="educationUnsufficient && !isMilitarySelected"
		>
			{{ $t('educationError') }}
		</ja-alert-bar>
		<footer class="sticky-footer">
			<div v-if="localSponsor" class="logo-wrapper">
				<ja-local-sponsor :logo="localSponsor"></ja-local-sponsor>
			</div>
			<ja-button
				class="small transparent left"
				name="back-shopping"
				:disabled="!educationUnsufficient || isMilitarySelected"
				:aria-label="$t('backToClusters')"
				@click.native.once="backToCareer"
			>
				<svgicon class="arrow-left" name="arrow-left"></svgicon>
				<span class="navigation-btn-text">
					{{ $t('back') }}
				</span>
			</ja-button>
			<ja-button
				name="finish-education"
				class="small right"
				:aria-label="$t('goGoalsPage')"
				:disabled="
					!selectedEducation || (educationUnsufficient && !isMilitarySelected)
				"
				@click.native.once="saveEducation"
			>
				{{ $t('next')
				}}<svgicon name="arrow-right" class="arrow-right"></svgicon>
			</ja-button>
		</footer>
	</div>
</template>
<script>
import '../components/icons/arrow-right';
import gql from 'graphql-tag';
import '../components/icons/headerIcons/education';
import '../components/icons/info';
import Button from '../components/ui/Button';
import AlertBar from '../components/ui/AlertBar';
import Header from '../components/ui/headers/Header';
import UserData from '../mixins/UserData';
import Accessibility from '../mixins/Accessibility';
import NavigationLoading from '../mixins/NavigationLoading';
import LocalSponsor from '@/components/ui/LocalSponsor';
import LocalSponsorService from '@/services/LocalSponsorService';

export default {
	components: {
		jaButton: Button,
		jaHeader: Header,
		jaAlertBar: AlertBar,
		jaLocalSponsor: LocalSponsor
	},
	mixins: [UserData, NavigationLoading, Accessibility],
	data() {
		return {
			educations: null,
			careerInfo: null,
			selectedEducation: null,
			selectedEducationTuition: null,
			educationUnsufficient: false,
			isMilitarySelected: false,
			studentLifeStageName: null,
			localSponsor: ''
		};
	},
	watch: {
		careerInfo: {
			handler(newCareerInfo) {
				if (newCareerInfo && newCareerInfo.cmsEducation) {
					const selectedEducation = this.educations.find(
						item =>
							item.hierarchyLevel === newCareerInfo.cmsEducation.hierarchyLevel
					);

					if (selectedEducation) {
						this.selectEducation(selectedEducation);
					}
				}
			},
			immediate: true,
			deep: true
		}
	},
	mounted() {
		LocalSponsorService.getLogo(
			'Education',
			this.userData.studentFacilityId
		).then(logo => (this.localSponsor = logo));
	},
	async created() {
		if (this.userData && this.careerInfo) {
			await this.$store.dispatch('getPauseState');
		}
	},
	methods: {
		selectEducation(education) {
			this.selectedEducation = education.education_ID;
			this.selectedEducationTuition = education.tuition;

			this.educationUnsufficient =
				education.hierarchyLevel < this.careerInfo.cmsEducation.hierarchyLevel;

			this.isMilitarySelected = education.name.toLowerCase() == 'military';

			if (this.educationUnsufficient) {
				if (this.isMilitarySelected) {
					setTimeout(() => {
						this.announceWarning(this.$t('militaryMsg'));
					}, 400);
				} else {
					setTimeout(() => {
						this.announceError(this.$t('educationError'));
					}, 400);
				}
			}
		},
		async saveEducation() {
			if (
				this.selectedEducation &&
				(this.isMilitarySelected || !this.educationUnsufficient)
			) {
				this.$store.commit('setLoading', true);
				const educationInfo = {
					id: this.userData.studentId,
					education_ID: this.selectedEducation
				};

				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation($update: StudentInputObjectGraphType!) {
							updateStudent(update: $update) {
								id
							}
						}
					`,
					variables: {
						update: educationInfo
					}
				});

				if (response.data) {
					this.$router.push({ name: 'goals' });
				}
			}
		},
		async backToCareer() {
			if (this.educationUnsufficient) {
				this.$store.commit('setLoading', true);
				const userInfo = {
					id: this.userData.studentId,
					career_ID: null
				};

				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation($update: StudentInputObjectGraphType!) {
							updateStudent(update: $update) {
								id
							}
						}
					`,
					variables: {
						update: userInfo
					}
				});

				if (response.data) {
					this.$router.push({
						name: 'clusters',
						params: {
							selectedCluster: this.careerInfo.careerCluster_ID
						}
					});
				}
			}
		}
	},
	apollo: {
		educations: {
			query: gql`
				{
					cmsEducations(order: "selectionDisplayOrder") {
						name
						selectionDisplayName
						education_ID
						hierarchyLevel
						tuition
					}
				}
			`,

			update: function(data) {
				return data.cmsEducations;
			},
			skip() {
				return !this.userData;
			}
		},
		careerInfo: {
			query: gql`
				query students($studentIdClause: String!) {
					students(where: $studentIdClause) {
						cmsCareer {
							careerCluster_ID
							cmsEducation {
								hierarchyLevel
							}
						}
					}
				}
			`,
			fetchPolicy: 'no-cache',
			variables() {
				return {
					studentIdClause: `id=${this.userData.studentId}`
				};
			},
			update: function(data) {
				if (data) {
					this.$store.commit('setLoading', false);
					return data.students[0].cmsCareer;
				}
			},
			skip() {
				return !this.userData;
			}
		},
		studentLifeStageName: {
			query: gql`
				query studentLifeStage($where: String!) {
					cmsLifeStages(where: $where) {
						name
					}
				}
			`,
			fetchPolicy: 'no-cache',
			variables() {
				return {
					where: `lifeStage_ID="${this.userData.studentLifeStageId}"`
				};
			},
			update: function(data) {
				if (data) {
					return data.cmsLifeStages[0].name;
				}
			},
			skip() {
				return !this.userData;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.education-wrapper {
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;

	content {
		flex-grow: 1;
		flex-shrink: 0;
		width: 100%;
		margin: auto;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding-bottom: 32px;

		.educations-cnt {
			height: 0;
			max-height: calc(100vh - #{$header-footer-combined-height});
			flex-grow: 1;
			flex-shrink: 0;
			width: 66%;
			min-width: 700px;
			max-width: 768px;
			padding: 32px;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;

			.education-grid {
				flex-grow: 1;
				flex-shrink: 0;
				display: grid;
				grid-template-columns: repeat(3, 1fr);
				grid-column-gap: 16px;
				grid-row-gap: 16px;
				width: 100%;
			}

			.education-card {
				display: flex;
				flex-direction: column;
				align-items: center;
				height: 100%;
				border: 1px solid #dde4e6;
				border-radius: 4px;
				background-color: #ffffff;
				box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);
				position: relative;
				padding: 16px 0 24px 0;

				.heading-4 {
					flex-grow: 1;
					flex-shrink: 0;
					margin: 0;
					text-align: center;
					padding: 0 16px 16px 16px;
				}
			}
		}
	}

	footer {
		justify-content: flex-end;

		.logo-wrapper {
			flex-grow: 1;
		}
	}
}
</style>
